import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import Swal from 'sweetalert2'

export function useModelActions(
  resourceApi,
  {
    showModal,
    isEditMode,
    entityData,
    viewAsCard,
    showDeleted,
    displayAsForm,
    loading,
    isFormVisible,
    keepFormOpen,
    resourceName
  }
) {
  const store = useStore()
  const itemsPerPage = ref(6)
  const currentPage = ref(1)
  const lastPage = ref(1)
  const additionalParams = ref({});
  const filters = ref({});

  const saveToLocalStorage = (key, data) => {
    localStorage.setItem(key, JSON.stringify(data))
  }

  const setAdditionalParams = (params) => {
    additionalParams.value = params;
  };

  const loadFromLocalStorage = key => {
    const data = localStorage.getItem(key)
    return data ? JSON.parse(data) : null
  }

  const openCreateModal = (defaultValues = {}) => {
    isEditMode.value = false
    entityData.value = { ...defaultValues }
    if (displayAsForm.value) {
      isFormVisible.value = true
    } else {
      showModal.value = true
    }
  }

  const openEditModal = entity => {
    // Enriquecer automáticamente los campos _id con sus correspondientes _name para MenuSelectedFull
    const enrichedEntity = { ...entity }
    
    Object.keys(enrichedEntity).forEach(key => {
      // Si el campo termina en _id y existe un campo correspondiente _name
      if (key.endsWith('_id') && enrichedEntity[key]) {
        const nameKey = key.replace('_id', '_name')
        if (enrichedEntity[nameKey]) {
          // Convertir el ID simple en objeto {id, label} para MenuSelectedFull
          enrichedEntity[key] = {
            id: enrichedEntity[key],
            label: enrichedEntity[nameKey]
          }
        }
      }
    })
    
    entityData.value = enrichedEntity
    isEditMode.value = true
    if (displayAsForm.value) {
      isFormVisible.value = true
    } else {
      showModal.value = true
    }
  }

  const handleEntityUpdated = updatedEntity => {
    if (!displayAsForm.value || !keepFormOpen.value) {
      showModal.value = false
      isFormVisible.value = false
    } else {
      entityData.value = { ...updatedEntity }
      isEditMode.value = true
    }
    fetchItems(currentPage.value)
  }

  const applyFilter = ({
    search,
    selectedFilter,
    filters = {},
  }) => {
    setAdditionalParams({
      ...filters,
    });
    fetchItems(1, search, selectedFilter)
  }

  const updateItemsPerPage = newItemsPerPage => {
    itemsPerPage.value = newItemsPerPage
    fetchItems(1)
  }

  const updateViewAsCard = view => {
    viewAsCard.value = view
  }

  const updateShowDeleted = show => {
    showDeleted.value = show
    fetchItems(1)
  }

  const restoreItem = async item => {
    try {
      const result = await Swal.fire({
        title: '¿Estás seguro?',
        html: `¿Deseas restaurar el ${resourceName.toLowerCase()}, <b>${item.name}</b>?`,
        icon: 'warning',
        showCancelButton: true,
        cancelButtonText: 'Cancelar',
        confirmButtonText: 'Sí, ¡restáuralo!',
        customClass: {
          confirmButton: 'btn bg-gradient-success',
          cancelButton: 'btn bg-gradient-danger'
        },
        buttonsStyling: false
      })

      if (result.isConfirmed) {
        await store.dispatch('modelStore/restoreRecord', {
          resource: resourceApi,
          itemId: item.id
        })
        fetchItems(currentPage.value)

        Swal.fire({
          title: '¡Restaurado!',
          text: `El ${resourceName.toLowerCase()} ha sido restaurado.`,
          icon: 'success',
          customClass: {
            confirmButton: 'btn bg-gradient-success'
          },
          buttonsStyling: false
        })
      }
    } catch (error) {
      console.error(
        `Error restaurando el ${resourceName.toLowerCase()}:`,
        error
      )
      Swal.fire({
        title: '¡Error!',
        text: `Hubo un problema al restaurar el ${resourceName.toLowerCase()}.`,
        icon: 'error',
        customClass: {
          confirmButton: 'btn bg-gradient-danger'
        },
        buttonsStyling: false
      })
    }
  }

  const deleteItem = async item => {
    try {
      const result = await Swal.fire({
        title: '¿Estás seguro?',
        html: `¡No podrás revertir esto!<br>¿Deseas eliminar el ${resourceName.toLowerCase()}, <b>${item.name}</b>?`,
        icon: 'warning',
        showCancelButton: true,
        cancelButtonText: 'Cancelar',
        confirmButtonText: 'Sí, ¡elimínalo!',
        customClass: {
          confirmButton: 'btn bg-gradient-success',
          cancelButton: 'btn bg-gradient-danger'
        },
        buttonsStyling: false
      })

      if (result.isConfirmed) {
        await store.dispatch('modelStore/deleteRecord', {
          resource: resourceApi,
          itemId: item.id
        })
        fetchItems(currentPage.value)

        Swal.fire({
          title: '¡Eliminado!',
          text: `El ${resourceName.toLowerCase()} ha sido eliminado.`,
          icon: 'success',
          customClass: {
            confirmButton: 'btn bg-gradient-success'
          },
          buttonsStyling: false
        })
      }
    } catch (error) {
      if (error.response && error.response.status === 403) {
        Swal.fire({
          title: '¡Error!',
          text: error.response.data.error,
          icon: 'error',
          customClass: {
            confirmButton: 'btn bg-gradient-danger'
          },
          buttonsStyling: false
        })
      } else {
        Swal.fire({
          title: '¡Error!',
          text: `Hubo un problema al eliminar el ${resourceName.toLowerCase()}.`,
          icon: 'error',
          customClass: {
            confirmButton: 'btn bg-gradient-danger'
          },
          buttonsStyling: false
        })
      }
    }
  }

  const fetchItems = async (
    page = 1,
    search = '',
    selectedFilter = 'updated_at'
  ) => {
    try {
      const response = await store.dispatch('modelStore/fetchRecords', {
        resource: resourceApi,
        options: {
          page,
          search,
          itemsPerPage: itemsPerPage.value,
          sortColumn: selectedFilter,
          sortOrder:
            selectedFilter === 'created_at' || selectedFilter === 'updated_at'
              ? 'desc'
              : 'asc',
          onlyDeleted: showDeleted.value
        },
        additionalParams: additionalParams.value
      })

      loading.value = false
      currentPage.value = response.data.pagination.current_page
      itemsPerPage.value = response.data.pagination.per_page
      lastPage.value = response.data.pagination.last_page

      // Guardar los datos en localStorage
      saveToLocalStorage(resourceApi, response.data)
    } catch (error) {
      if (error.response && error.response.status === 403) {
        Swal.fire({
          title: '¡Error!',
          text: error.response.data.message,
          icon: 'error',
          customClass: {
            confirmButton: 'btn bg-gradient-danger'
          },
          buttonsStyling: false
        })
      } else {
        Swal.fire({
          title: '¡Error!',
          text: `Hubo un problema al obtener los registros de ${resourceName.toLowerCase()}.`,
          icon: 'error',
          customClass: {
            confirmButton: 'btn bg-gradient-danger'
          },
          buttonsStyling: false
        })

        // Intentar cargar los datos desde localStorage en caso de error
        const localData = loadFromLocalStorage(resourceApi)
        if (localData && localData.pagination) {
          loading.value = false
          currentPage.value = localData.pagination.current_page
          itemsPerPage.value = localData.pagination.per_page
          lastPage.value = localData.pagination.last_page
          store.commit('modelStore/SET_ITEMS', localData.data)
          store.commit('modelStore/SET_PAGINATION', localData.pagination)
        }
      }
    }
  }

  const changePage = page => {
    currentPage.value = page
    fetchItems(page)
  }

  const filteredItems = computed(() => {
    return store.getters['modelStore/items'] || []
  })

  const totalFilteredItems = computed(() => {
    return store.getters['modelStore/totalItems'] || 0
  })

  return {
    showModal,
    isEditMode,
    entityData,
    itemsPerPage,
    viewAsCard,
    showDeleted,
    displayAsForm,
    currentPage,
    loading,
    openCreateModal,
    openEditModal,
    handleEntityUpdated,
    applyFilter,
    updateItemsPerPage,
    updateViewAsCard,
    updateShowDeleted,
    restoreItem,
    deleteItem,
    fetchItems,
    changePage,
    setAdditionalParams,
    filteredItems,
    totalFilteredItems,
    lastPage,
    isFormVisible,
    keepFormOpen,
    filters
  }
}